import { ButtonV2 } from '@labrav/react-components'
import React, { useState } from 'react'
import { useStyles } from './CreateMeteringFileUploader.styles'
import {
  FileUploadWithLoader,
  defaultNow,
  bucketName,
  isNonEmptyNumber,
  updateAppAlert,
  useCurrentCloudProvider,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { useDispatch, useSelector } from '../../../../../store/hooks'
import {
  postMeteringFile,
  azurePostMeteringFile,
} from '../../../../../api/marketplace/index'
import { Typography } from '@mui/material'
import { getApiErrorData } from '../../../../../utils/apiErrorResponse'
import {
  parseCsvUploadErrors,
  type CsvRowError,
} from '../../../../../utils/parseCsvUploadErrors'

export default function CreateMeteringFileUploader({
  updateModalVisibility,
  fileUploadType,
}: {
  updateModalVisibility: () => void
  fileUploadType: 'csv' | 'excel'
}): JSX.Element {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [files, setFiles] = useState('')
  const [fileObj, setFileObj] = useState<File | null>(null)
  const [errors, setErrors] = useState<CsvRowError[] | string[]>([])
  const [loading, setLoading] = useState(false)
  const partnerId = useSelector<string>(
    state => state.AccountData?.user?.partnerData?.partnerId || ''
  )
  const acceptedFormats = fileUploadType === 'csv' ? ['.csv'] : ['.xlsx']
  const selectedCloud = useCurrentCloudProvider()
  const isCsv = fileUploadType === 'csv'
  const isParsedErrors = errors.length > 0 && typeof errors[0] !== 'string'

  const handleSubmit = () => {
    if (files) {
      setLoading(true)
      setErrors([])
      ;(async () => {
        try {
          if (selectedCloud === 'AZURE') {
            if (!fileObj) {
              setErrors([
                'File could not be read for upload. Please remove the file and upload again.',
              ])
              return
            }
            const requestId = `client-${Date.now()}`
            await azurePostMeteringFile(fileObj, partnerId, requestId)
          } else {
            await postMeteringFile(files)
          }
          dispatch(
            updateAppAlert({
              message: 'Metering record submitted successfully.',
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
          updateModalVisibility()
        } catch (err: unknown) {
          const data = getApiErrorData(err)
          const rawErrors = data?.errors ?? ['Upload failed. Please try again.']
          const parsed = parseCsvUploadErrors(rawErrors)

          setErrors(parsed.length > 0 ? parsed : rawErrors)
          setFiles('')
          setFileObj(null)
        } finally {
          setLoading(false)
        }
      })()
    }
  }
  return (
    <>
      <div
        className={classes.uploader}
        data-testId="createMetering-fileUploader"
      >
        <FileUploadWithLoader
          keyName="upload metering file"
          partnerId={partnerId}
          existingFileLinks={isEmpty(files) ? [] : [files]}
          assetType="metering"
          folder="metering_files"
          fileUploadDescription={`The file must be in .${
            fileUploadType === 'csv' ? 'CSV' : 'XLSX'
          } format and must be less than 1mb in size.`}
          quantity={1}
          fileUploadType={isCsv ? 'txt/csv' : 'xlsx'}
          unit="MB"
          uploadFileSize={1}
          acceptedFormats={acceptedFormats}
          updateUrl={url => {
            setFiles(url)
          }}
          onUploadComplete={(_url, file) => setFileObj(file)}
          deleteUrl={() => {
            setFiles('')
            setFileObj(null)
          }}
          defaultNow={defaultNow}
          fileUploadButtonText="Upload file"
          fileFormatIncorrectText={
            'Failed to upload the file. Format is incorrect.'
          }
          fileUploadContainerTitle="Drag and drop file here"
          isMultiFileUpload={false}
          updateErr={() => null}
          uploadError={undefined}
          bucketName={bucketName}
          checkDuplicate
          showRealFileName
        />
      </div>
      {errors.length > 0 && (
        <div className={classes.errorContainer}>
          <Typography className={classes.errorTitle}>
            {isParsedErrors
              ? 'We found issues in your file. Please fix them and re-upload.'
              : 'Upload failed'}
          </Typography>

          {!isParsedErrors ? (
            <ul className={classes.errorList}>
              {(errors as string[]).map((error, index) => (
                <li key={index}>
                  <Typography component="span" className={classes.errors}>
                    {error}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : (
            (errors as CsvRowError[]).map(({ row, fieldErrors }, index) => (
              <div key={index} className={classes.errorGroup}>
                <Typography fontWeight={600} className={classes.rowTitle}>
                  {isNonEmptyNumber(row) ? `Row ${row}` : 'Header'}
                </Typography>
                <ul className={classes.errorList}>
                  {fieldErrors.map(({ field, message }, j) => (
                    <li key={j}>
                      <Typography component="span" className={classes.errors}>
                        <strong>{field}:</strong> {message}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
      <div className={classes.footer}>
        <ButtonV2
          className={classes.cancelButton}
          onClick={updateModalVisibility}
        >
          Cancel
        </ButtonV2>
        <ButtonV2
          data-testId="createMetering-fileUploaderSubmitButton"
          loading={loading}
          disabled={files ? false : true}
          onClick={() => handleSubmit()}
        >
          Submit
        </ButtonV2>
      </div>
    </>
  )
}
